import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { HeartIcon } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { loadStoredEmail } from '@/lib/userEmail'
import {
  trackLocationWatchmanSubscribed,
  trackLocationWatchmanUnsubscribed,
  trackWatchHeartEmailPromptOpened,
} from '@/lib/track'
import { LocationWatchmanDialog } from '@/features/accommodation/detail/components/location-watchman/LocationWatchmanDialog'
import { subscribeLocationWatchman } from '@/features/accommodation/detail/components/location-watchman/subscribeLocationWatchman'
import { unsubscribeUnitWatchman } from './unsubscribeUnitWatchman'
import { markUnitWatched, unmarkUnitWatched, useUnitWatchEmail } from './watchedUnits'

interface WatchHeartButtonProps {
  slug: string
  unitId: number
  /** Where the heart sits, tracked with every toggle. */
  source: string
  /**
   * `overlay` — a bare heart over an offer photo (cards).
   * `pill` — a round translucent button for the mobile detail top bar.
   * `link` — an icon + label link next to "Sdílet" on the desktop detail.
   */
  variant?: 'overlay' | 'pill' | 'link'
  className?: string
}

/**
 * Airbnb-style heart that toggles watching a unit's free dates. With a known
 * e-mail one click subscribes; without one it asks for it first.
 */
export function WatchHeartButton({ slug, unitId, source, variant = 'overlay', className }: WatchHeartButtonProps) {
  const watchEmail = useUnitWatchEmail(unitId)
  const [pending, setPending] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const watched = watchEmail !== undefined

  const openEmailPrompt = () => {
    trackWatchHeartEmailPromptOpened(slug, unitId, source)
    setDialogOpen(true)
  }

  const unwatch = async (email: string) => {
    unmarkUnitWatched(unitId)
    trackLocationWatchmanUnsubscribed(slug, unitId, source)
    await unsubscribeUnitWatchman(unitId, email)
  }

  const toggle = async () => {
    if (pending) return

    if (watched) {
      setPending(true)
      try {
        await unwatch(watchEmail)
        toast('Termíny už nehlídáme.')
      } catch (error) {
        markUnitWatched(unitId, watchEmail)
        Sentry.captureException(error)
        toast.error('Hlídání se nepodařilo zrušit, zkuste to prosím znovu.')
      } finally {
        setPending(false)
      }
      return
    }

    const email = loadStoredEmail()
    if (!email) {
      openEmailPrompt()
      return
    }

    setPending(true)
    try {
      await subscribeLocationWatchman(unitId, email)
      markUnitWatched(unitId, email)
      void trackLocationWatchmanSubscribed(slug, unitId, email, source)
      toast.success('Hlídáme volné termíny', {
        description: `Jakmile se uvolní termín, napíšeme vám na ${email}.`,
        action: {
          label: 'Jiný e-mail',
          onClick: () => {
            unwatch(email).catch((error: unknown) => Sentry.captureException(error))
            openEmailPrompt()
          },
        },
      })
    } catch (error) {
      Sentry.captureException(error)
      toast.error('Hlídání se nepodařilo zapnout, zkuste to prosím znovu.')
    } finally {
      setPending(false)
    }
  }

  const label = watched ? 'Přestat hlídat termíny' : 'Hlídat volné termíny'

  const heart = (
    <HeartIcon
      className={cn(
        'transition-colors',
        variant === 'overlay'
          ? cn('size-7 stroke-white stroke-2 drop-shadow-sm', watched ? 'fill-red' : 'fill-black/40')
          : cn(
              'size-5',
              watched && 'fill-red text-red',
              !watched && (variant === 'pill' ? 'text-slate-700' : 'text-red'),
            ),
      )}
    />
  )

  return (
    // The heart sits inside offer-card links: keep clicks, including those
    // bubbling up from the portalled e-mail dialog, from reaching the link.
    // A pure event boundary with no semantics of its own, hence presentation.
    <span role="presentation" className="contents" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={watched}
        title={label}
        disabled={pending}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          void toggle()
        }}
        className={cn(
          'cursor-pointer transition-transform active:scale-90 disabled:cursor-wait',
          variant === 'overlay' && 'p-1',
          variant === 'pill' && 'flex size-10 items-center justify-center rounded-full bg-white/70 backdrop-blur-sm',
          variant === 'link' && 'text-red inline-flex items-center gap-1.5 underline',
          className,
        )}
      >
        {heart}
        {variant === 'link' && (watched ? 'Hlídáte termíny' : 'Hlídat termíny')}
      </button>
      <LocationWatchmanDialog
        slug={slug}
        unitId={unitId}
        source={source}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </span>
  )
}
